import React, { useEffect, useMemo, useState } from 'react';
import './ActionCard.css';
import { useNavigate } from 'react-router-dom';
import { getCachedCollectionData } from '../homePageService';
import Shimmer from '../../common/Shimmer';
import { truncateToTwoDecimals } from '../../dms_common/DmsUtils';
import { EnvironmentRegistry } from '../../../services/registry/environmentRegistry';
import { useDispatch } from 'react-redux';
import { setDayEndPopup } from '../../../stateManagement/actions/dayEndActions';
import { getDayEndStatusFromSession } from '../../dms_common/DayEndUtils';

interface OutletCreditSummary {
  id: string | number;
  outletCode: string;
  outletName: string;
  status: string;
  creditLimit: number; // total credit limit
  creditAmount: number; // outstanding amount
}


const formatName = (name: string): string => {
  if (name.length > 15) {
    return name.substring(0, 12) + '...';
  }
  return name;
};

const ActionCard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState<OutletCreditSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  

  const formatCurrencyK = (amount: number): string => {
    try {
      if(amount === 0) return `${ConfigDictionary?.currency} 0`;
      else{
        const thousands = truncateToTwoDecimals((typeof amount === 'number' ? amount : 0) / 1000);
        return `${ConfigDictionary?.currency} ${thousands}K`;
      }
    }
    catch {
      return `${ConfigDictionary?.currency} 0`;
    }
  };
  const currentLob = EnvironmentRegistry.getCurrentLob() || "";
  const baseLob = currentLob.replace(/(demo|uat)$/i, '');

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        if(baseLob === "cokesa") return;
        setLoading(true);
        const result = await getCachedCollectionData(undefined, "UNPAID");
        if (!isMounted) return;
        const normalized: OutletCreditSummary[] = (Array.isArray(result) ? result : []).map((item: any) => ({
          id: item?.id ?? item?.outletCode,
          outletCode: item?.outletCode ?? '',
          outletName: item?.outletName ?? '',
          status: (item?.status ?? '').toString(),
          creditLimit: typeof item?.creditLimit === 'number' ? item.creditLimit : 0,
          creditAmount: typeof item?.creditAmount === 'number' ? item.creditAmount : 0,
        }));
        setData(normalized);
      } catch (e) {
        console.error('Failed to load outlet credit data', e);
        setData([]);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const unpaid = useMemo(() => {
    return data.filter((d) => (d.status || '').toUpperCase() === 'UNPAID');
  }, [data]);

  const headerStats = useMemo(() => {
    const totalOutstanding = unpaid.reduce((sum, o) => sum + (o.creditAmount || 0), 0);
    const uniqueCount = Array.from(
      new Set(unpaid.map((o) => o.outletCode || o.id))
    ).length;
    return { totalOutstanding, uniqueCount };
  }, [unpaid]);

  const topThree = useMemo(() => {
    const sorted = [...unpaid].sort((a, b) => {
      const byOutstanding = (b.creditAmount || 0) - (a.creditAmount || 0);
      if (byOutstanding !== 0) return byOutstanding;
      return (b.creditLimit || 0) - (a.creditLimit || 0);
    });
    return sorted.slice(0, 3);
  }, [unpaid]);

  const dayEndObj = getDayEndStatusFromSession();
  const handleClick = (task: string) => {
    if (dayEndObj && dayEndObj.isDayEndInProgress){
      dispatch(setDayEndPopup(true));
      return;
    }
    if (task === "viewAllCollections") {
      navigate('/dashboard/collection');
    }

  }

  return (
    <div className="outlets-payment-card">
      <div className="outlets-payment-header">
        <div className="outlets-payment-title">
          <div className="outlets-payment-icon">
            {loading ? (
              <Shimmer width="18px" height="18px" borderRadius="50%" />
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', color: '#D55151' }}>
                <img src={require('../../../assets/images/png/due.svg').default} alt="Danger" style={{ width: 18, height: 18 }} />
              </span>
            )}
          </div>
          <span className="outlets-payment-amount">
            {loading ? (
              <Shimmer width="220px" height="20px" />
            ) : (
              <>
                <span style={{ fontWeight: 700 }}>{formatCurrencyK(headerStats.totalOutstanding)}</span> is due for {headerStats.uniqueCount} Outlets
              </>
            )}
          </span>
        </div>
        {/* {loading ? (
          <Shimmer width="120px" height="36px" className="shimmer-button" />
        ) : (
          <button className="outlets-payment-btn" onClick={() => navigate('/dashboard/collection')}>Send reminders</button>
        )} */}
      </div>

      <div className="outlets-list">
        {loading && (
          <>
            {[1, 2, 3].map((item) => (
              <div key={`shimmer-${item}`} className="outlet-item">
                <Shimmer width="120px" height="18px" className="shimmer-text" />

                <div className="outlet-metrics">
                  <div className="outlet-metric">
                    <Shimmer width="80px" height="14px" className="shimmer-text" />
                    <Shimmer width="60px" height="16px" className="shimmer-text" />
                  </div>

                  <div className="outlet-metric">
                    <Shimmer width="100px" height="14px" className="shimmer-text" />
                    <Shimmer width="70px" height="16px" className="shimmer-text" />
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
        {!loading && unpaid.length === 0 && (
          <div className="outlet-item">
            <div className="outlet-name">Collections data not available</div>
          </div>
        )}
        {!loading && topThree.map((outlet) => (
          <div key={outlet.id} className="outlet-item">
            <div className="outlet-name" title = {outlet.outletName}>{formatName(outlet.outletName)}</div>
            <div className="outlet-metrics">
              <div className="outlet-metric">
                <span className="outlet-metric-label">Credit Limit</span>
                <span className="outlet-metric-value">{formatCurrencyK(outlet.creditLimit)}</span>
              </div>

              <div className="outlet-metric">
                <span className="outlet-metric-label">Outstanding Amount</span>
                <span className="outlet-metric-value outstanding">{formatCurrencyK(outlet.creditAmount)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="outlets-payment-footer">
        {loading ? (
          <Shimmer width="140px" height="16px" />
        ) : (
          unpaid.length > 0 ? (
            <a href="#" className="view-outlets-link" onClick={(e) => { e.preventDefault(); handleClick("viewAllCollections") }}>View All Collections</a>
          ) : null
        )}
      </div>
    </div>
  );
};

export default ActionCard;